import { ReportStatus } from "./interfaces/ReportStatus";

// Single source of truth for how a report status is presented to users. The
// report cards, the Status filter dropdown, and the active-filter chips all read
// from here so the three surfaces can never drift apart again.

export type StatusPaletteKey =
  | "warning"
  | "info"
  | "error"
  | "success"
  | "primary";

// Canonical user-facing label for a status. `isCoView` (the viewer holds the
// station-manage permission) only changes the two CO-track statuses whose
// wording differs for a reviewer vs. the authoring officer.
export const reportStatusLabel = (
  status: ReportStatus,
  isCoView = false,
): string => {
  switch (status) {
    case ReportStatus.AWAITING_INPUT:
    case ReportStatus.AWAITING_TRANSCRIPT_DECISION:
      return "Action needed";
    case ReportStatus.PENDING_REVIEW:
      return "Ready to review";
    case ReportStatus.GENERATING:
      return "Generating";
    case ReportStatus.SIGNED:
      return "Signed";
    case ReportStatus.IN_CO_REVIEW:
      return isCoView ? "Needs review" : "Submitted for Commanding Officer review";
    case ReportStatus.CHANGES_REQUESTED:
      return isCoView ? "Rejected" : "Changes requested";
    case ReportStatus.CO_APPROVED:
      return "Approved by Commanding Officer";
    case ReportStatus.RMS_COMMITTED:
      return "Submitted to Records System";
    default:
      return status;
  }
};

// Semantic theme-palette key for a status, mirroring the report card's accent
// colors so the filter and the cards speak one color language.
export const reportStatusPalette = (
  status: ReportStatus,
): StatusPaletteKey => {
  switch (status) {
    case ReportStatus.PENDING_REVIEW:
    case ReportStatus.AWAITING_INPUT:
    case ReportStatus.AWAITING_TRANSCRIPT_DECISION:
      return "warning";
    case ReportStatus.SIGNED:
    case ReportStatus.IN_CO_REVIEW:
      return "info";
    case ReportStatus.CHANGES_REQUESTED:
      return "error";
    case ReportStatus.CO_APPROVED:
    case ReportStatus.RMS_COMMITTED:
      return "success";
    default:
      return "primary";
  }
};
